/**
 * Route Codes
 *
 * Centralized registry of all route codes used in the application.
 * Organized by functional module for maintainability.
 *
 * Structure:
 * - Grouped by features/modules (DASHBOARD, WIZARD, etc.)
 * - Each code is a unique string constant
 * - Combined into ROUTE_CODES export for universal access
 *
 * Usage:
 * - Used for route configuration, permissions, and navigation logic
 * - Referenced in route-icons.js and route-screens.js
 *
 * Maintenance:
 * - Add new codes under the appropriate module section
 * - Never modify existing codes (breaks routing, unless its not being registered in db)
 */

const DASHBOARD_CODES = {
  DASHBOARD: "DASHBOARD",
};

const WIZARD_CODES = {
  SUPPORT_PLAN: "SUPPORT_PLAN",
  CLIENT_SUPPORT_PLAN: "CLIENT_SUPPORT_PLAN",
  ASSIGNED_CLIENT: "ASSIGNED_CLIENT",
  BTN_ASSIGNED_CLIENT_TABLET_CONFIG_TAB: "BTN_ASSIGNED_CLIENT_TABLET_CONFIG_TAB",
  SUPPORT_CIRCLE: "SUPPORT_CIRCLE",
  CS_REPRESENTATIVE: "CS_REPRESENTATIVE",
  EMERGENCY_PLANNING: "EMERGENCY_PLANNING",
  PERSONAL_SUPPORT_DETAILS: "PERSONAL_SUPPORT_DETAILS",
  CARE_PLAN_ACTIONS: "CARE_PLAN_ACTIONS",
  CARE_PLAN_CALENDER: "CARE_PLAN_CALENDER",
  BTN_CARE_PLAN_FUTURE_CALENDER: "BTN_CARE_PLAN_FUTURE_CALENDER",
  BTN_CARE_PLAN_ARCHIVED_CALENDER: "BTN_CARE_PLAN_ARCHIVED_CALENDER",
  CALENDER_HEAT_MAP: "CALENDER_HEAT_MAP",
  TECHNOLOGY_DETAILS: "TECHNOLOGY_DETAILS",
  ALERT_CONDITIONS: "ALERT_CONDITIONS",
  HEALTH_INFORMATION: "HEALTH_INFORMATION",
  GENERATE_DOCUMENT: "GENERATE_DOCUMENT",
  SUSPENSION_HISTORY: "SUSPENSION_HISTORY",
  SUPPORT_PLAN_ACKNOWLEDGEMENT_HISTORY: "SUPPORT_PLAN_ACKNOWLEDGEMENT_HISTORY",
  SUPPORT_PLAN_INDIVIDUAL_VERSION_HISTORY:
    "SUPPORT_PLAN_INDIVIDUAL_VERSION_HISTORY",
  SATISFACTION_SCORES: "SATISFACTION_SCORES",
  INDIVIDUAL_COMMUNICATIONS_REPORT: "INDIVIDUAL_COMMUNICATIONS_REPORT",
  BILLING_HISTORY: "BILLING_HISTORY",
  MUI_INCIDENT_HISTORY: "MUI_INCIDENT_HISTORY",
  OBJECTIVE_CHECKLIST_REPORT: "OBJECTIVE_CHECKLIST_REPORT",
  SUPPORT_NOTE_HISTORY_AUDIT: "SUPPORT_NOTE_HISTORY_AUDIT",
  SUPPORT_PLAN_VIEWERS: "SUPPORT_PLAN_VIEWERS",
};

const SUSPENSION_MANAGER_CODES = {
  SUSPENSION_MANAGER: "SUSPENSION_MANAGER",
  BTN_ADD_NEW_SUSPENSION: "BTN_ADD_NEW_SUSPENSION",
  BTN_CLEAN_UP_REMOVAL_QUEUE: "BTN_CLEAN_UP_REMOVAL_QUEUE",
};

const FS_TOOLS_CODES = {
  FS_TOOLS: "FS_TOOLS",
  GEOCOMM_UTILITIES: "GEOCOMM_UTILITIES",
  GEOCOMM_MAINTENANCE: "GEOCOMM_MAINTENANCE",
  GEOCOMM_WRISTBAND: "GEOCOMM_WRISTBAND",
  PROVISION_GEOCOMM_DEVICE: "PROVISION_GEOCOMM_DEVICE",
  WRISTBAND_CONTACTS: "WRISTBAND_CONTACTS",
  WRISTBAND_GEOFENCE: "WRISTBAND_GEOFENCE",
  GENERATE_SHORT_TOKEN: "GENERATE_SHORT_TOKEN",
  REBOOT_WRISTBAND: "REBOOT_WRISTBAND",
  SHINOBI_CAMERA: "SHINOBI_CAMERA",
  SHINOBI_CAMERA_CONFIG: "SHINOBI_CAMERA_CONFIG",
  SUPPORT_CENTER_TABS: "SUPPORT_CENTER_TABS",
  FOSCAM_CAMERA: "FOSCAM_CAMERA",
  FOSCAM_CAMERA_CONFIG: "FOSCAM_CAMERA_CONFIG",
  IGUARD_STOVE: "IGUARD_STOVE",
  PROVISION_IGUARD_DEVICE: "PROVISION_IGUARD_DEVICE",
  SENSOR_SYSTEM_UTILITIES: "SENSOR_SYSTEM_UTILITIES",
  PROVISION_SENSOR_SYSTEM: "PROVISION_SENSOR_SYSTEM",
  SENSOR_STATUS_TOOLS: "SENSOR_STATUS_TOOLS",
  SENSOR_SYSTEM_MESSAGE_STATUS: "SENSOR_SYSTEM_MESSAGE_STATUS",
  SENSOR_OUTAGE_STATUS: "SENSOR_OUTAGE_STATUS",
  IUS_SENSOR_STATUS: "IUS_SENSOR_STATUS",
  TABLET_DASHBOARD: "TABLET_DASHBOARD",
  BATTERY_LEVEL: "BATTERY_LEVEL",
  DEVICE_GRID: "DEVICE_GRID",
  TABLET_UTILITIES: "TABLET_UTILITIES",
  TABLET_MAINTENANCE: "TABLET_MAINTENANCE",
  TABLET_USERS: "TABLET_USERS",
};

const BILLING_TOOLS_CODES = {
  BILLING_TOOLS: "BILLING_TOOLS",
  EMBS_FILE_MANAGEMENT: "EMBS_FILE_MANAGEMENT",
  UPLOAD_WEEKLY_EMBS_FILE: "UPLOAD_WEEKLY_EMBS_FILE",
  GENERATE_EMBS_SUBMISSION_FILE: "GENERATE_EMBS_SUBMISSION_FILE",
  EMBS_CLIENT_MANAGEMENT: "EMBS_CLIENT_MANAGEMENT",
  EDIT_BILLING_PARAMETERS: "EDIT_BILLING_PARAMETERS",
  BILLING_DATA: "BILLING_DATA",
  BILLING_WEEK_SUMMARY: "BILLING_WEEK_SUMMARY",
  CREATE_EMBS_HOURS_FILE: "CREATE_EMBS_HOURS_FILE",
  LIST_IMPORTED_FILES: "LIST_IMPORTED_FILES",
};

const SENSOR_MANAGER_CODES = {
  SENSOR_MANAGER: "SENSOR_MANAGER",
};

const APPLICATION_SETTINGS_CODES = {
  SETTINGS: "SETTINGS",
  APPLICATION_SETTINGS: "APPLICATION_SETTINGS",
  SPECIAL_HANDLING_MANAGER: "SPECIAL_HANDLING_MANAGER",
  GLOBAL_ANNOUNCEMENTS: "GLOBAL_ANNOUNCEMENTS",
  COMMUNICATION_METHODS: "COMMUNICATION_METHODS",
  USER_ADMINISTRATION: "USER_ADMINISTRATION",
  USER_ADMINISTRATION_MAINTENANCE: "USER_ADMINISTRATION_MAINTENANCE",
  SSO_ASSOCIATION_MAINTENANCE: "SSO_ASSOCIATION_MAINTENANCE",
  APPLICATION_ADMINISTRATION: "APPLICATION_ADMINISTRATION",
  USER_RIGHTS_MAINTENANCE: "USER_RIGHTS_MAINTENANCE",
  USER_ROLES_MAINTENANCE: "USER_ROLES_MAINTENANCE",
  SYSTEM_TAG_MAINTENANCE: "SYSTEM_TAG_MAINTENANCE",
  TABLET_NOTIFICATION_CONFIGURATION: "TABLET_NOTIFICATION_CONFIGURATION",
  APPLICATION_LOGS: "APPLICATION_LOGS",
  SUPPORT_PLAN_LOGS: "SUPPORT_PLAN_LOGS",
  ADMIN_USER_SUPPORT_PLAN_LOGS: "ADMIN_USER_SUPPORT_PLAN_LOGS",
  QOL_ADMINISTRATION: "QOL_ADMINISTRATION",
  QOL_MAINTENANCE: "QOL_MAINTENANCE",
  ANSWER_MAINTENANCE: "ANSWER_MAINTENANCE",
  OBJ_ADMINISTRATION: "OBJ_ADMINISTRATION",
  OBJECTIVES_MAINTENANCE: "OBJECTIVES_MAINTENANCE",
  CHECKLIST_ADMIN: "CHECKLIST_ADMIN",
  CHECKLIST_MAINTENANCE: "CHECKLIST_MAINTENANCE",
  FIVE9_ADMIN: "FIVE9_ADMIN",
  UNVERIFIED_NUMBER_MAINTENANCE: "UNVERIFIED_NUMBER_MAINTENANCE",
  DASHBOARD_ADMIN: "DASHBOARD_ADMIN",
  DASHBOARD_PAGE_MAINTENANCE: "DASHBOARD_PAGE_MAINTENANCE",
  DASHBOARD_FUNCTION_MAINTENANCE: "DASHBOARD_FUNCTION_MAINTENANCE",
  DASHBOARD_FUNCTION_PARAMS_OPTIONS: "DASHBOARD_FUNCTION_PARAMS_OPTIONS",
  DASHBOARD_OBJECT_TYPES: "DASHBOARD_OBJECT-TYPES",
  DASHBOARD_PAGE_OBJECTS: "DASHBOARD_PAGE_OBJECTS",
  DASHBOARD_PAGE_BUILDER: "DASHBOARD_PAGE_BUILDER",
  SENTIMENT_ADMIN: "SENTIMENT_ADMIN",
  SENTIMENT_MAINTENANCE: "SENTIMENT_MAINTENANCE",
  MUI_REPORT_DATA_TYPES: "MUI_REPORT_DATA_TYPES",
  MUI_REPORT_FIELDS: "MUI_REPORT_FIELDS",
  MUI_REPORT_TAB_CONFIG: "MUI_REPORT_TAB_CONFIG",
  MUI_REPORT_PROFILES: "MUI_REPORT_PROFILES",
  MUI_REPORT_TAGS: "MUI_REPORT_TAGS",
  SUPPORT_PLAN_TEMPLATE_MAINTENANCE: "SUPPORT_PLAN_TEMPLATE_MAINTENANCE"
};

const IUS_SENSOR_CODES = {
  IUS_SENSOR: "IUS_SENSOR",
};

const AUTH_FROM_TOKEN_CODES = {
  AUTH_FROM_TOKEN: "AUTH_FROM_TOKEN",
};

const CALENDAR_VIEWER_CODES = {
  CALENDAR_VIEWER: "CALENDAR_VIEWER",
};

export const ROUTE_CODES = {
  ...DASHBOARD_CODES,
  ...WIZARD_CODES,
  ...SUSPENSION_MANAGER_CODES,
  ...FS_TOOLS_CODES,
  ...BILLING_TOOLS_CODES,
  ...SENSOR_MANAGER_CODES,
  ...APPLICATION_SETTINGS_CODES,
  ...IUS_SENSOR_CODES,
  ...AUTH_FROM_TOKEN_CODES,
  ...CALENDAR_VIEWER_CODES,
};
